import { defineChart } from '..'
import type { LabelOption } from 'echarts/types/src/util/types'
import type { SankeySeriesOption } from 'echarts'

export default defineChart<
    [
        Import[] | undefined,
        Import | undefined,
        (value: number) => string,
        string,
        string
    ]
>((data, currentData, formatter, rootText, title) => {
    if (!data || !currentData) return

    /**
     * Use metadata.
     */
    const { findProduct } = useMeta()

    /**
     * Use countries.
     */
    const { getName, getColor } = useCountries()

    /**
     * Use params.
     */
    const { config } = useParams()

    /**
     * Filter products if needed.
     */
    const values = config.importProducts?.length
        ? currentData.products.filter(({ product_id }) => {
              return config.importProducts?.includes(product_id)
          })
        : currentData.products

    /**
     * Get an import total value.
     */
    function getImportTotalValue(
        values: Import['products'],
        productId?: number
    ) {
        const filteredValues =
            productId === undefined
                ? values
                : values.filter(({ product_id: id }) => id === productId)

        return filteredValues.reduce((acc, curr) => {
            return acc + curr.import_value
        }, 0)
    }

    /**
     * Total amount of imports.
     */
    const totalImportValue = getImportTotalValue(
        data.flatMap(({ products }) => products)
    )

    /**
     * Get all products.
     */
    const products = [
        ...new Set(values.flatMap(({ product_id }) => product_id))
    ]

    /**
     * Whether the country is the current country.
     */
    function isCurrentCountry(country: string) {
        return country === currentData?.country
    }

    /**
     * Max label width.
     */
    const maxLabelWidth = 100

    return {
        series: {
            type: 'sankey',
            draggable: false,
            animation: false,
            silent: true,
            nodeWidth: 5,
            layoutIterations: 0,
            itemStyle: {
                color: 'black'
            },
            lineStyle: {
                color: twColor('dark-gray')
            },
            label: {
                color: 'black'
            },
            right: maxLabelWidth,
            left: maxLabelWidth,
            data: [
                {
                    name: 'root',
                    label: {
                        position: 'left',
                        align: 'right',
                        ...createRootLabel(
                            rootText,
                            formatter(totalImportValue),
                            maxLabelWidth
                        )
                    }
                },
                ...data.map(importValue => ({
                    name: importValue.country,
                    depth: 1,
                    itemStyle: isCurrentCountry(importValue.country)
                        ? {
                              color: getColor(importValue.country)
                          }
                        : {},
                    label: createLabel(
                        getName(importValue.country) || '',
                        formatter(getImportTotalValue(importValue.products)),
                        isCurrentCountry(importValue.country)
                    )
                })),
                ...products.map(productId => ({
                    name: String(productId),
                    depth: 2,
                    itemStyle: {
                        color: findProduct(productId)?.color
                    },
                    label: createLabel(
                        findProduct(productId)?.name || '',
                        formatter(getImportTotalValue(values, productId))
                    )
                })),
                ...values.map(importValue => ({
                    name: `${importValue.product_id}-${importValue.origin_country}`,
                    depth: 3,
                    itemStyle: {
                        color: getColor(importValue.origin_country)
                    },
                    label: {
                        width: maxLabelWidth,
                        overflow: 'truncate' as const,
                        formatter: getName(importValue.origin_country)
                    }
                }))
            ] satisfies SankeySeriesOption['data'],
            links: [
                ...data.map(importValue => ({
                    source: 'root',
                    target: importValue.country,
                    value: getImportTotalValue(importValue.products),
                    lineStyle: isCurrentCountry(importValue.country)
                        ? {
                              color: getColor(importValue.country)
                          }
                        : {}
                })),
                ...values.map(importValue => ({
                    source: currentData.country,
                    target: String(importValue.product_id),
                    value: importValue.import_value,
                    lineStyle: {
                        color: findProduct(importValue.product_id)?.color
                    }
                })),
                ...values.map(importValue => ({
                    source: String(importValue.product_id),
                    target: `${importValue.product_id}-${importValue.origin_country}`,
                    value: importValue.import_value,
                    lineStyle: {
                        color: findProduct(importValue.product_id)?.color
                    }
                }))
            ] satisfies SankeySeriesOption['links']
        },
        export: {
            title: {
                text: title
            },
            series: {
                top: '10%',
                bottom: '10%'
            }
        },
        media: [
            {
                query: {
                    maxWidth: 400
                },
                option: {
                    series: {
                        left: 0,
                        right: 0,
                        label: {
                            show: false
                        }
                    }
                }
            },
            {
                query: {
                    minWidth: 400
                },
                option: {
                    series: {
                        left: maxLabelWidth,
                        right: maxLabelWidth,
                        label: {
                            show: true
                        }
                    }
                }
            }
        ]
    }
})

/**
 * Create a node label.
 */
function createLabel(name: string, value: string, show = true) {
    return {
        show: show === false ? false : undefined,
        color: 'black',
        formatter: `{value|${value}} - {name|${name}}`,
        overflow: 'break',
        rich: {
            value: {
                fontWeight: 'bold',
                fontSize: 16,
                fontFamily: 'Rubik'
            },
            text: {
                fontSize: 12,
                fontFamily: 'Source Sans Pro',
                overflow: 'break'
            }
        }
    } as LabelOption
}

/**
 * Create a node label.
 */
function createRootLabel(
    name: string,
    value: string,
    maxWidth: number,
    show = true
) {
    return {
        show: show === false ? false : undefined,
        color: 'black',
        formatter: `{value|${value}}\n\n{name|${name}}`,
        width: maxWidth,
        overflow: 'break',
        rich: {
            value: {
                fontWeight: 'bold',
                fontSize: 16,
                fontFamily: 'Rubik',
                padding: [0, 0, 5, 0]
            },
            text: {
                fontSize: 12,
                fontFamily: 'Source Sans Pro',
                overflow: 'break'
            }
        }
    } as LabelOption
}
